import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash2, KeyRound, ShieldCheck, User as UserIcon, Phone, Mail, X, Clock, Coffee, CalendarRange, Loader2, Download, Save, Pencil, Lock, LockOpen, Plus } from "lucide-react";

function ShiftEditRow({ shift, onSaved, onDeleted }) {
  const [ci, setCi] = useState(shift.clock_in_et || "");
  const [co, setCo] = useState(shift.clock_out_et || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const dirty = ci !== (shift.clock_in_et || "") || co !== (shift.clock_out_et || "");
  const locked = shift.locked;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/timeclock/shift/${shift.id}`, { clock_in_et: ci, clock_out_et: co });
      onSaved(data);
      toast.success("Shift updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save shift"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Void the ${shift.date} shift? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/timeclock/shift/${shift.id}`);
      onDeleted(shift.id);
      toast.success("Shift voided");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not void shift"); }
    finally { setDeleting(false); }
  };
  return (
    <tr data-testid={`shift-row-${shift.id}`} className="border-b border-slate-100">
      <td className="px-2 py-2 font-semibold text-slate-700 whitespace-nowrap">{shift.date}
        {shift.manual && <span className="ml-1 rounded bg-indigo-100 px-1 py-0.5 text-[9px] font-bold text-indigo-700">Manual</span>}
      </td>
      <td className="px-2 py-2">
        <input data-testid={`shift-in-${shift.id}`} type="datetime-local" value={ci} disabled={locked} onChange={(e) => setCi(e.target.value)}
          className="rounded border border-slate-300 px-1.5 py-1 text-[12px] focus:border-teal focus:outline-none disabled:bg-slate-100 disabled:text-slate-400" />
      </td>
      <td className="px-2 py-2">
        <input data-testid={`shift-out-${shift.id}`} type="datetime-local" value={co} disabled={locked} onChange={(e) => setCo(e.target.value)}
          className="rounded border border-slate-300 px-1.5 py-1 text-[12px] focus:border-teal focus:outline-none disabled:bg-slate-100 disabled:text-slate-400" />
        {!shift.clock_out && <span className="ml-1 text-[10px] font-semibold text-amber-600">open</span>}
      </td>
      <td className="px-2 py-2 text-right font-bold text-[#1A395E] tabular-nums">{shift.work_hours}</td>
      <td className="px-2 py-2 text-right text-amber-700 tabular-nums">{shift.break_hours}</td>
      <td className="px-2 py-2 text-center">{shift.auto_clocked_out ? <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-700">Auto</span> : "—"}</td>
      <td className="px-2 py-2 text-right">
        {locked ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400"><Lock className="h-3 w-3" /> Locked</span>
        ) : (
          <div className="inline-flex items-center gap-1.5">
            <button data-testid={`shift-save-${shift.id}`} onClick={save} disabled={!dirty || saving}
              className="inline-flex items-center gap-1 rounded-md bg-[#1A395E] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[#24497A] transition-colors disabled:opacity-40">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
            </button>
            <button data-testid={`shift-void-${shift.id}`} onClick={del} disabled={deleting} title="Void this shift"
              className="inline-flex items-center rounded-md border border-rose-200 p-1 text-rose-500 hover:bg-rose-50 transition-colors disabled:opacity-40">
              {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function ManualShiftForm({ userId, onAdded }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [ci, setCi] = useState(`${today}T09:00`);
  const [co, setCo] = useState(`${today}T17:00`);
  const [saving, setSaving] = useState(false);
  const add = async () => {
    setSaving(true);
    try {
      await api.post("/timeclock/shift", { user_id: userId, date, clock_in_et: ci, clock_out_et: co });
      toast.success("Shift added");
      onAdded();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add shift"); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="manual-shift-form" className="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-[#1A395E]"><Plus className="h-3.5 w-3.5" /> Add a missing shift (forgot to clock in)</div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-[11px] font-semibold text-slate-500">Date
          <input data-testid="manual-date" type="date" value={date} onChange={(e) => { setDate(e.target.value); setCi(`${e.target.value}T09:00`); setCo(`${e.target.value}T17:00`); }}
            className="mt-0.5 block rounded border border-slate-300 px-2 py-1 text-[12px] focus:border-teal focus:outline-none" /></label>
        <label className="text-[11px] font-semibold text-slate-500">Clock In (ET)
          <input data-testid="manual-in" type="datetime-local" value={ci} onChange={(e) => setCi(e.target.value)}
            className="mt-0.5 block rounded border border-slate-300 px-2 py-1 text-[12px] focus:border-teal focus:outline-none" /></label>
        <label className="text-[11px] font-semibold text-slate-500">Clock Out (ET)
          <input data-testid="manual-out" type="datetime-local" value={co} onChange={(e) => setCo(e.target.value)}
            className="mt-0.5 block rounded border border-slate-300 px-2 py-1 text-[12px] focus:border-teal focus:outline-none" /></label>
        <button data-testid="manual-add-btn" onClick={add} disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-teal px-3 py-1.5 text-[12px] font-semibold text-teal-foreground hover:bg-teal/90 transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add shift
        </button>
      </div>
    </div>
  );
}

function AttendanceReport() {
  const [range, setRange] = useState("today");
  const [data, setData] = useState({ rows: [], dates: [] });
  const [loading, setLoading] = useState(true);
  const [otDaily, setOtDaily] = useState(8);
  const [otWeekly, setOtWeekly] = useState(40);
  const otLimit = range === "week" ? otWeekly : otDaily;
  const isOT = (r) => otLimit > 0 && (r.work_seconds || 0) / 3600 > otLimit;
  const [histUser, setHistUser] = useState(null);
  const [hist, setHist] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const openHistory = async (r) => {
    setHistUser(r); setHist(null); setHistLoading(true);
    try { const { data } = await api.get(`/timeclock/shifts/${r.user_id}?days=30`); setHist(data); }
    catch { toast.error("Could not load shift history"); }
    finally { setHistLoading(false); }
  };
  const onShiftSaved = (updated) => setHist((h) => h ? { ...h, shifts: h.shifts.map((s) => s.id === updated.id ? { ...updated, locked: s.locked } : s) } : h);
  const onShiftDeleted = (id) => setHist((h) => h ? { ...h, shifts: h.shifts.filter((s) => s.id !== id) } : h);
  const [approval, setApproval] = useState({ current_week_start: "", approved: [] });
  const [auditLog, setAuditLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const loadApprovals = async () => {
    try {
      const [a, l] = await Promise.all([api.get("/timeclock/approvals"), api.get("/timeclock/approval-log?limit=50")]);
      setApproval(a.data); setAuditLog(l.data.log || []);
    } catch { /* noop */ }
  };
  useEffect(() => { loadApprovals(); }, []);
  const currentApproved = (approval.approved || []).some((a) => a.week_start === approval.current_week_start);
  const [approving, setApproving] = useState(false);
  const toggleApprove = async () => {
    setApproving(true);
    try {
      if (currentApproved) { await api.delete(`/timeclock/approve-week/${approval.current_week_start}`); toast.success("Week unlocked"); }
      else { await api.post("/timeclock/approve-week", {}); toast.success("Week approved & locked for payroll"); }
      await loadApprovals();
      if (histUser) openHistory(histUser);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update approval"); }
    finally { setApproving(false); }
  };
  const load = async (r) => {
    setLoading(true);
    try { const { data } = await api.get(`/timeclock/attendance?range=${r}`); setData(data); }
    catch { toast.error("Could not load attendance"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(range); }, [range]);
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/timeclock/attendance/export?range=${range}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `timesheet_${range}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Timesheet exported");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };
  const stateChip = (s) => ({
    working: "bg-emerald-100 text-emerald-700", on_break: "bg-amber-100 text-amber-700", clocked_out: "bg-slate-100 text-slate-500",
  }[s] || "bg-slate-100 text-slate-500");
  const stateLabel = (s) => ({ working: "On clock", on_break: "On break", clocked_out: "Off" }[s] || "Off");
  return (
    <div data-testid="attendance-report" className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-teal" />
          <h2 className="text-lg font-700 text-slate-800">Attendance Report</h2>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-500">
            OT over
            <input data-testid="attendance-ot-input" type="number" min="0" step="1"
              value={range === "week" ? otWeekly : otDaily}
              onChange={(e) => { const v = Number(e.target.value); range === "week" ? setOtWeekly(v) : setOtDaily(v); }}
              className="w-16 rounded-md border border-slate-300 px-2 py-1 text-[12px] focus:border-teal focus:outline-none" />
            <span className="text-slate-400 font-normal">hrs/{range === "week" ? "wk" : "day"}</span>
          </label>
          <div className="inline-flex overflow-hidden rounded-lg border border-slate-300" data-testid="attendance-range">
            {[["today", "Today"], ["week", "Last 7 days"]].map(([val, lbl]) => (
              <button key={val} data-testid={`attendance-range-${val}`} onClick={() => setRange(val)}
                className={`px-3 py-1.5 text-[12px] font-semibold transition-colors ${range === val ? "bg-[#1A395E] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {val === "week" ? <span className="inline-flex items-center gap-1"><CalendarRange className="h-3.5 w-3.5" /> {lbl}</span> : lbl}
              </button>
            ))}
          </div>
          <button data-testid="attendance-export-btn" onClick={exportCsv} disabled={exporting}
            className="inline-flex items-center gap-1.5 rounded-lg border border-teal/60 bg-white px-3 py-1.5 text-[12px] font-semibold text-teal hover:bg-teal/5 transition-colors disabled:opacity-60">
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Export CSV
          </button>
          <button data-testid="approve-week-btn" onClick={toggleApprove} disabled={approving}
            title={`Week of ${approval.current_week_start}`}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-60 ${currentApproved ? "bg-emerald-600 text-white hover:bg-emerald-700" : "border border-[#1A395E]/50 bg-white text-[#1A395E] hover:bg-[#1A395E]/5"}`}>
            {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : currentApproved ? <LockOpen className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            {currentApproved ? "Approved ✓ · Unlock" : "Approve week"}
          </button>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Employee</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-right font-semibold">Worked{range === "week" ? " (7d)" : ""}</th>
                <th className="px-3 py-2 text-right font-semibold">Break</th>
                {range === "week" && <th className="px-3 py-2 text-right font-semibold">Days</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No employees.</td></tr>}
              {data.rows.map((r) => (
                <tr key={r.user_id} data-testid={`attendance-row-${r.user_id}`} onClick={() => openHistory(r)}
                  className={`cursor-pointer ${isOT(r) ? "bg-rose-50/70 hover:bg-rose-100/70" : "hover:bg-slate-50/60"}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-800">{r.name}</div>
                    <div className="text-[11px] text-slate-400">{r.job_role || (r.role === "admin" ? "Admin" : "Employee")}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${stateChip(r.today_state)}`}>{stateLabel(r.today_state)}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-bold text-[#1A395E] tabular-nums">
                    <span className="inline-flex items-center gap-1.5">
                      {isOT(r) && <span data-testid={`attendance-ot-badge-${r.user_id}`} title={`Over ${otLimit} hrs`} className="rounded bg-rose-600 px-1.5 py-0.5 text-[9px] font-bold text-white">OT</span>}
                      {r.work_hours}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-amber-700 tabular-nums"><span className="inline-flex items-center gap-1"><Coffee className="h-3 w-3" /> {r.break_hours}</span></td>
                  {range === "week" && <td className="px-3 py-2 text-right text-slate-500 tabular-nums">{r.days_worked}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3">
        <button data-testid="approval-log-toggle" onClick={() => setShowLog((v) => !v)}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-500 hover:text-[#1A395E]">
          <ShieldCheck className="h-3.5 w-3.5" /> Approval history {showLog ? "▲" : "▼"}
          {auditLog.length > 0 && <span className="rounded-full bg-slate-100 px-1.5 text-[10px] text-slate-500">{auditLog.length}</span>}
        </button>
        {showLog && (
          <div data-testid="approval-log" className="mt-2 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-2">
            {auditLog.length === 0 ? (
              <p className="px-2 py-3 text-[12px] text-slate-400">No approvals recorded yet.</p>
            ) : auditLog.map((e, i) => (
              <div key={i} data-testid={`approval-log-row-${i}`} className="flex items-center justify-between gap-3 border-b border-slate-100 px-2 py-1.5 last:border-0 text-[12px]">
                <span className="flex items-center gap-1.5">
                  {e.action === "approved"
                    ? <Lock className="h-3.5 w-3.5 text-emerald-600" />
                    : <LockOpen className="h-3.5 w-3.5 text-amber-600" />}
                  <b className={e.action === "approved" ? "text-emerald-700" : "text-amber-700"}>{e.action === "approved" ? "Approved" : "Unlocked"}</b>
                  <span className="text-slate-500">week of {e.week_start}</span>
                </span>
                <span className="text-slate-400">{e.by_name}{e.at ? ` · ${new Date(e.at).toLocaleString()}` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {histUser && (
        <div data-testid="shift-history-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setHistUser(null)}>
          <div className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-100 bg-white px-5 py-3">
              <div>
                <h3 className="text-lg font-700 text-[#1A395E]">{histUser.name} — Shift History</h3>
                <p className="text-[12px] text-slate-400">Last 30 days · edit clock-in/out to correct times (ET){hist ? ` · Total ${hist.total_work_hours}` : ""}</p>
              </div>
              <button data-testid="shift-history-close" onClick={() => setHistUser(null)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4">
              {histLoading ? (
                <div className="flex items-center gap-2 py-8 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
              ) : !hist || hist.shifts.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">No shifts in the last 30 days.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wide text-slate-500">
                    <tr className="border-b border-slate-200">
                      <th className="px-2 py-2 text-left">Date</th>
                      <th className="px-2 py-2 text-left">Clock In (ET)</th>
                      <th className="px-2 py-2 text-left">Clock Out (ET)</th>
                      <th className="px-2 py-2 text-right">Worked</th>
                      <th className="px-2 py-2 text-right">Break</th>
                      <th className="px-2 py-2 text-center">Auto</th>
                      <th className="px-2 py-2 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {hist.shifts.map((s) => <ShiftEditRow key={s.id} shift={s} onSaved={onShiftSaved} onDeleted={onShiftDeleted} />)}
                  </tbody>
                </table>
              )}
              {hist && !histLoading && <ManualShiftForm userId={histUser.user_id} onAdded={() => openHistory(histUser)} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee", personal_email: "", whatsapp_number: "", job_role: "", allowed_states: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [contact, setContact] = useState({ personal_email: "", whatsapp_number: "", job_role: "", allowed_states: "" });
  const [savingContact, setSavingContact] = useState(false);

  const parseStates = (s) => (s || "").split(",").map((x) => x.trim().toUpperCase()).filter(Boolean);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("Name, email and password are required"); return; }
    setSaving(true);
    try {
      await api.post("/users", { ...form, allowed_states: parseStates(form.allowed_states) });
      toast.success(`${form.role === "admin" ? "Admin" : "Employee"} account created`);
      setForm({ name: "", email: "", password: "", role: "employee", personal_email: "", whatsapp_number: "", job_role: "", allowed_states: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create user"); }
    finally { setSaving(false); }
  };

  const openContact = (u) => {
    setEditUser(u);
    setContact({ personal_email: u.personal_email || "", whatsapp_number: u.whatsapp_number || "", job_role: u.job_role || "", allowed_states: (u.allowed_states || []).join(", ") });
  };

  const saveContact = async () => {
    setSavingContact(true);
    try {
      await api.patch(`/users/${editUser.id}/profile`, { ...contact, allowed_states: parseStates(contact.allowed_states) });
      toast.success("User settings saved");
      setEditUser(null);
      load();
    } catch { toast.error("Could not save settings"); }
    finally { setSavingContact(false); }
  };

  const resetPw = async (u) => {
    const pw = window.prompt(`Set a new password for ${u.email}:`);
    if (!pw) return;
    try { await api.post(`/users/${u.id}/reset-password`, { password: pw }); toast.success("Password updated"); }
    catch { toast.error("Reset failed"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? Any orders assigned to them will be unassigned.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="ws-light space-y-6 -m-6 p-6 bg-slate-100 min-h-[calc(100vh-3.5rem)] text-slate-800">
      <div>
        <h1 className="font-display text-3xl font-700 tracking-tight text-slate-800">Team &amp; Access</h1>
        <p className="text-sm text-slate-500 mt-1">Create employee and admin accounts, set their login passwords, and manage access.</p>
      </div>

      <AttendanceReport />

      <div className="grid grid-cols-12 gap-6">
        {/* Create form */}
        <div className="col-span-12 lg:col-span-4">
          <div className="rounded-lg border border-slate-200 bg-white p-5 sticky top-24">
            <div className="flex items-center gap-2 mb-4">
              <UserPlus className="h-4 w-4 text-slate-500" />
              <h3 className="font-display text-lg font-600">Add User</h3>
            </div>
            <form onSubmit={create} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Full Name</label>
                <input data-testid="add-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none" placeholder="Jane Doe" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Email</label>
                <input data-testid="add-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none" placeholder="jane@titlehub.com" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Password</label>
                <input data-testid="add-user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-teal focus:outline-none" placeholder="Set a login password" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Personal Email <span className="text-slate-500 font-normal">(notifications)</span></label>
                <input data-testid="add-user-personal-email" type="email" value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none" placeholder="jane@personal.com" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">WhatsApp Number <span className="text-slate-500 font-normal">(notifications)</span></label>
                <input data-testid="add-user-whatsapp" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-teal focus:outline-none" placeholder="+15551234567" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Role</label>
                <select data-testid="add-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none">
                  <option value="employee">Employee (processes orders, PII masked)</option>
                  <option value="admin">Admin (full access)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Job Role <span className="text-slate-500 font-normal">(searcher / typing)</span></label>
                <select data-testid="add-user-job-role" value={form.job_role} onChange={(e) => setForm({ ...form, job_role: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none">
                  <option value="">— None —</option>
                  <option value="Title Searcher">Title Searcher</option>
                  <option value="Typing and Commitment Processor">Typing and Commitment Processor</option>
                  <option value="Typing and Searcher">Typing and Searcher</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">Allowed States <span className="text-slate-500 font-normal">(blank = all)</span></label>
                <input data-testid="add-user-allowed-states" value={form.allowed_states} onChange={(e) => setForm({ ...form, allowed_states: e.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono uppercase focus:ring-2 focus:ring-teal focus:outline-none" placeholder="e.g. MA, MD, NH" />
                <p className="text-[11px] text-slate-500 mt-1">Comma-separated 2-letter states. Searcher can only be assigned orders in these states.</p>
              </div>
              <button data-testid="add-user-submit" type="submit" disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-navy-2 transition-colors duration-200 disabled:opacity-50">
                <UserPlus className="h-4 w-4" /> {saving ? "Creating…" : "Create User"}
              </button>
            </form>
          </div>
        </div>

        {/* Users table */}
        <div className="col-span-12 lg:col-span-8">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white max-w-full">
            <table className="w-full table-fixed text-left">
              <colgroup>
                <col className="w-[20%]" /><col className="w-[24%]" /><col className="w-[20%]" /><col className="w-[20%]" /><col className="w-[16%]" />
              </colgroup>
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2 font-semibold">Name</th>
                  <th className="px-3 py-2 font-semibold">Email</th>
                  <th className="px-3 py-2 font-semibold">Notify Contacts</th>
                  <th className="px-3 py-2 font-semibold">Role</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {loading && (
                  <tr><td colSpan={5} className="px-3 py-10 text-center text-sm text-slate-500">Loading…</td></tr>
                )}
                {!loading && users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50 transition-colors duration-200">
                    <td className="px-3 py-3 text-sm font-medium text-slate-800">
                      <span className="flex items-center gap-2 min-w-0"><UserIcon className="h-3.5 w-3.5 text-slate-600 shrink-0" /> <span className="truncate">{u.name}</span></span>
                    </td>
                    <td className="px-3 py-3 text-[13px] font-mono text-slate-600 truncate" title={u.email}>{u.email}</td>
                    <td className="px-3 py-3 text-[12px] text-slate-600">
                      <div className="space-y-0.5 min-w-0" data-testid={`user-contacts-${u.id}`}>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Mail className="h-3 w-3 text-slate-500 shrink-0" />
                          {u.personal_email ? <span className="font-mono truncate" title={u.personal_email}>{u.personal_email}</span> : <span className="text-slate-500">—</span>}
                        </div>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Phone className="h-3 w-3 text-slate-500 shrink-0" />
                          {u.whatsapp_number ? <span className="font-mono truncate" title={u.whatsapp_number}>{u.whatsapp_number}</span> : <span className="text-slate-500">—</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                        u.role === "admin" ? "text-blue-700 bg-blue-100 border-blue-300" : "text-slate-600 bg-slate-100 border-slate-200"}`}>
                        {u.role === "admin" && <ShieldCheck className="h-3 w-3" />} {u.role}
                      </span>
                      {u.job_role && <div data-testid={`user-jobrole-${u.id}`} className="text-[11px] text-slate-500 mt-1">{u.job_role}</div>}
                      {(u.allowed_states || []).length > 0 && (
                        <div data-testid={`user-states-${u.id}`} className="mt-1 flex flex-wrap gap-1">
                          {u.allowed_states.map((s) => <span key={s} className="rounded bg-teal/15 px-1.5 py-0.5 text-[9px] font-bold text-teal">{s}</span>)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button data-testid={`edit-contact-${u.id}`} onClick={() => openContact(u)} title="Contact & role settings"
                          className="inline-flex items-center justify-center rounded-md border border-slate-300 h-7 w-7 text-slate-600 hover:bg-slate-100 transition-colors">
                          <Phone className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`reset-pw-${u.id}`} onClick={() => resetPw(u)} title="Reset password"
                          className="inline-flex items-center justify-center rounded-md border border-slate-300 h-7 w-7 text-slate-600 hover:bg-slate-100 transition-colors">
                          <KeyRound className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)} disabled={u.id === user?.id} title="Delete user"
                          className="inline-flex items-center justify-center rounded-md border border-red-300 h-7 w-7 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditUser(null)}>
          <div data-testid="contact-modal" className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-600 flex items-center gap-2"><Phone className="h-4 w-4 text-teal" /> User Settings</h3>
              <button data-testid="contact-modal-close" onClick={() => setEditUser(null)} className="text-slate-500 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-xs text-slate-500 mb-4">Notification contacts + job role &amp; state coverage for <span className="font-semibold text-slate-600">{editUser.name}</span>.</p>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Personal Email</label>
            <input data-testid="contact-personal-email" type="email" value={contact.personal_email}
              onChange={(e) => setContact({ ...contact, personal_email: e.target.value })} placeholder="name@personal.com"
              className="w-full mb-4 rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none" />
            <label className="text-xs font-semibold text-slate-600 mb-1 block">WhatsApp Number</label>
            <input data-testid="contact-whatsapp" value={contact.whatsapp_number}
              onChange={(e) => setContact({ ...contact, whatsapp_number: e.target.value })} placeholder="+15551234567"
              className="w-full mb-4 rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-teal focus:outline-none" />
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Job Role</label>
            <select data-testid="contact-job-role" value={contact.job_role} onChange={(e) => setContact({ ...contact, job_role: e.target.value })}
              className="w-full mb-4 rounded-md border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none">
              <option value="">— None —</option>
              <option value="Title Searcher">Title Searcher</option>
              <option value="Typing and Commitment Processor">Typing and Commitment Processor</option>
              <option value="Typing and Searcher">Typing and Searcher</option>
            </select>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Allowed States <span className="text-slate-500 font-normal">(blank = all)</span></label>
            <input data-testid="contact-allowed-states" value={contact.allowed_states}
              onChange={(e) => setContact({ ...contact, allowed_states: e.target.value })} placeholder="e.g. MA, MD, NH"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono uppercase focus:ring-2 focus:ring-teal focus:outline-none" />
            <p className="text-[11px] text-slate-500 mt-1">Comma-separated 2-letter states — searcher can only be assigned orders in these states.</p>
            <div className="mt-5 flex justify-end">
              <button data-testid="contact-save" onClick={saveContact} disabled={savingContact}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-navy-2 transition-colors disabled:opacity-60">
                {savingContact ? "Saving…" : "Save Settings"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
